import React, { useEffect, useId, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Alert, Box, Button, Collapse, Skeleton, Typography } from '@mui/material';
import { ChevronDown, Circle, CircleCheck, CircleDot, CircleMinus, Clock } from 'lucide-react';
import { StatusPill, useDelayedFlag } from '../ui';
import { sbg } from '../../theme/sbgTokens';
import { apiSlice } from '../../store/api/apiSlice';
import { TimeOffEntry, useGetWfhApprovalRouteQuery } from '../../store/slices/timeOffSlice';
import { formatDateTime } from '../../utils/format';
import {
  deadlineLine,
  deliveryNote,
  noticeLabel,
  reviewersLine,
  routeHeadline,
  routeSteps,
  RouteStepState,
} from './wfhRoute';

export interface WfhApprovalRouteProps {
  entry: TimeOffEntry;
  /**
   * `sheet` (review drawers) draws the route as steps under its own heading.
   * `card` sits inside a card that already has a title and a stage rail.
   */
  layout?: 'sheet' | 'card';
}

const STEP_ICON: Record<RouteStepState, React.ReactNode> = {
  done: <CircleCheck size={16} aria-hidden="true" />,
  current: <CircleDot size={16} aria-hidden="true" />,
  upcoming: <Circle size={16} aria-hidden="true" />,
  skipped: <CircleMinus size={16} aria-hidden="true" />,
};

const STEP_COLOR: Record<RouteStepState, string> = {
  done: sbg.action.primary,
  current: sbg.action.primary,
  upcoming: sbg.border.strong,
  skipped: sbg.text.secondary,
};

const STEP_STATE_WORDS: Record<RouteStepState, string> = {
  done: 'done',
  current: 'current step',
  upcoming: 'not yet',
  skipped: 'skipped',
};

/**
 * How often an open route re-reads the server while it's on screen. Claims
 * lapse, deadlines pass and VPs decide from email links, none of which this
 * browser sees otherwise.
 */
const OPEN_ROUTE_POLL_MS = 30_000;

/** Stages nothing moves on from, so there's nothing to poll for. */
const DONE_STAGES = new Set(['auto_approved', 'approved', 'declined', 'withdrawn', 'canceled']);

const RouteSection: React.FC<WfhApprovalRouteProps> = ({ entry, layout = 'sheet' }) => {
  const [stillOpen, setStillOpen] = useState(true);
  // currentData, not data: while the sheet moves to another request, the
  // previous request's route must not show under the new one's buttons.
  const { currentData: route, isLoading, isError, refetch, isFetching } = useGetWfhApprovalRouteQuery(entry._id, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    pollingInterval: stillOpen ? OPEN_ROUTE_POLL_MS : 0,
    skipPollingIfUnfocused: true,
  });
  const [emailsOpen, setEmailsOpen] = useState(false);
  const ids = useId();
  const showSkeleton = useDelayedFlag(isLoading);
  useEffect(() => {
    // Anything still pending can change from another session, HR's included.
    if (route) setStillOpen(!DONE_STAGES.has(route.stage));
  }, [route]);

  // The route re-reads the server; the request around it (the status pill,
  // the stage rail, the decision buttons) doesn't. When the server's row is
  // newer than this screen's copy, refetch everything that shows the request:
  // once per change, so a copy that can't refresh never loops.
  const dispatch = useDispatch();
  const refreshedFor = useRef<string | null>(null);
  const serverUpdatedAt = route?.entryUpdatedAt ?? null;
  useEffect(() => {
    if (!serverUpdatedAt || !entry.updatedAt || refreshedFor.current === serverUpdatedAt) return;
    if (new Date(serverUpdatedAt).getTime() > new Date(entry.updatedAt).getTime()) {
      refreshedFor.current = serverUpdatedAt;
      dispatch(apiSlice.util.invalidateTags(['TimeOff']));
    }
  }, [serverUpdatedAt, entry.updatedAt, dispatch]);

  if (isLoading) {
    return (
      <Box role="status" aria-label="Loading where this request is" aria-busy="true" sx={{ visibility: showSkeleton ? 'visible' : 'hidden' }}>
        <Skeleton variant="rounded" height={96} sx={{ borderRadius: sbg.radius.lg }} />
      </Box>
    );
  }
  // A failed refresh keeps the last route on screen (with a note below); only
  // a route that never loaded gets the retry.
  if (isError && !route) {
    return (
      <Alert
        severity="warning"
        action={(
          <Button size="small" variant="text" onClick={() => refetch()} disabled={isFetching}>
            Retry
          </Button>
        )}
      >
        Couldn’t load where this request is. Retry, or open the request again.
      </Alert>
    );
  }
  if (!route) return null;

  const headline = routeHeadline(route);
  const deadline = deadlineLine(route);
  const reviewers = reviewersLine(route, entry);
  const steps = layout === 'sheet' ? routeSteps(route, entry) : [];
  const titleId = `${ids}-title`;
  const emailsId = `${ids}-emails`;

  return (
    <Box component="section" aria-labelledby={layout === 'sheet' ? titleId : undefined} aria-label={layout === 'card' ? 'Approval route' : undefined} sx={{ display: 'grid', gap: 1.5 }}>
      {layout === 'sheet' && (
        <Typography id={titleId} variant="h4" component="h3" sx={{ color: sbg.text.primary }}>
          Approval route
        </Typography>
      )}

      <Box sx={{ display: 'grid', gap: 0.75, p: 2, borderRadius: sbg.radius.lg, bgcolor: sbg.bg.surfaceSubtle }}>
        <Box>
          <StatusPill tone={headline.tone}>{headline.pill}</StatusPill>
        </Box>
        <Typography variant="labelMd" component="p" sx={{ color: sbg.text.primary }}>
          {headline.title}
        </Typography>
        {headline.body && (
          <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary }}>
            {headline.body}
          </Typography>
        )}
        {deadline && (
          <Typography
            variant="body2"
            component="p"
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, color: route.deadline?.overdue ? sbg.status.warning.text : sbg.text.secondary }}
          >
            <Box component="span" sx={{ display: 'inline-flex', mt: '1px', flex: 'none' }}><Clock size={14} aria-hidden="true" /></Box>
            {deadline}
          </Typography>
        )}
      </Box>

      {isError && (
        <Typography variant="body2" component="p" role="status" sx={{ color: sbg.status.warning.text }}>
          Couldn’t refresh just now. This is the last state loaded.
        </Typography>
      )}

      {steps.length > 0 && (
        <Box component="ol" aria-label="Approval steps" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid' }}>
          {steps.map((step, index) => (
            <Box
              component="li"
              key={step.label}
              aria-current={step.state === 'current' ? 'step' : undefined}
              sx={{ display: 'grid', gridTemplateColumns: '16px minmax(0, 1fr)', columnGap: 1.25 }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box component="span" sx={{ display: 'inline-flex', color: STEP_COLOR[step.state], mt: '2px' }}>
                  {STEP_ICON[step.state]}
                </Box>
                {index < steps.length - 1 && (
                  <Box aria-hidden="true" sx={{ flex: 1, width: '1px', minHeight: 12, my: 0.5, bgcolor: sbg.border.default }} />
                )}
              </Box>
              <Box sx={{ pb: index < steps.length - 1 ? 1.25 : 0, minWidth: 0 }}>
                <Typography
                  variant="labelSm"
                  component="p"
                  sx={{ color: step.state === 'upcoming' || step.state === 'skipped' ? sbg.text.secondary : sbg.text.primary }}
                >
                  {step.label}
                  <span className="sbg-sr-only">{`, ${STEP_STATE_WORDS[step.state]}`}</span>
                </Typography>
                {step.detail && (
                  <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, overflowWrap: 'anywhere' }}>
                    {step.detail}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {reviewers && (
        <Box sx={{ display: 'grid', gap: 0.25 }}>
          <Typography variant="labelSm" component="p" sx={{ color: sbg.text.primary }}>Who can decide</Typography>
          <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary }}>{reviewers}</Typography>
        </Box>
      )}
      {route.reviewersProblem && <Alert severity="warning">{route.reviewersProblem}</Alert>}

      {route.notices.length > 0 && (
        <Box sx={{ display: 'grid', gap: 1 }}>
          <Button
            variant="text"
            size="small"
            onClick={() => setEmailsOpen((open) => !open)}
            aria-expanded={emailsOpen}
            aria-controls={emailsId}
            endIcon={<Box component="span" sx={{ display: 'inline-flex', transition: 'transform 160ms', transform: emailsOpen ? 'rotate(180deg)' : 'none' }}><ChevronDown size={16} /></Box>}
            sx={{ justifySelf: 'start', ml: -1.25 }}
          >
            {`Emails sent (${route.notices.length})`}
          </Button>
          <Collapse in={emailsOpen} unmountOnExit>
            <Box component="ul" id={emailsId} aria-label="Emails sent for this request" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1.25 }}>
              {route.notices.map((notice) => {
                const note = deliveryNote(notice.recipients);
                return (
                  <Box component="li" key={`${notice.kind}-${notice.at}`} sx={{ display: 'grid', gap: 0.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="labelSm" component="p" sx={{ color: sbg.text.primary }}>{noticeLabel(notice.kind)}</Typography>
                      {note && <StatusPill tone={note.tone}>{note.text}</StatusPill>}
                    </Box>
                    <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, overflowWrap: 'anywhere' }}>
                      {`${formatDateTime(notice.at)} · To ${notice.recipients.map((recipient) => recipient.name).join(', ')}`}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

/**
 * Where a WFH request is in its approval route: who has it now, who can
 * decide, when a VP decision is due, and every email the route sent. Renders
 * nothing (and loads nothing) for other request types.
 */
const WfhApprovalRoute: React.FC<WfhApprovalRouteProps> = (props) =>
  // Keyed by request, so moving the HR sheet to the next request starts fresh.
  props.entry.type === 'WFH' ? <RouteSection key={props.entry._id} {...props} /> : null;

export default WfhApprovalRoute;
